import { useEffect, useState } from "react"
import { doc, onSnapshot } from "firebase/firestore"
import { db } from "@/lib/firebase"
import type { PortfolioItem } from "@/types/models"

export function useFlashDetail(flashId: string | undefined) {
  const [flash, setFlash] = useState<PortfolioItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [collectionName, setCollectionName] = useState<"flashes" | "flashesArchived">("flashes")

  useEffect(() => {
    if (!flashId) {
      setFlash(null)
      setLoading(false)
      return
    }

    setLoading(true)
    let unsubscribe: (() => void) | undefined
    
    // Try to load from flashes first
    const unsubscribeFlashes = onSnapshot(
      doc(db, "flashes", flashId),
      (flashDoc) => {
        if (flashDoc.exists()) {
          const data = flashDoc.data()
          const flashData: PortfolioItem = {
            id: flashDoc.id,
            artistId: data.artistId as string,
            type: (data.type as PortfolioItem["type"]) ?? "image",
            storagePath: (data.storagePath as string) ?? "",
            imageUrl: data.imageUrl as string,
            compressedImageUrl: data.compressedImageUrl as string | undefined,
            width: (data.width as number) ?? 0,
            height: (data.height as number) ?? 0,
            styleTags: (data.styleTags as string[]) ?? [],
            caption: (data.caption as string) ?? "",
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
            isFlash: true,
            minPrice: data.minPrice as string,
            minSize: data.minSize as string,
            fotw: data.fotw as boolean,
            fotwWeek: data.fotwWeek as number,
            votes: (data.votes as string[]) ?? [],
            accessCode: data.accessCode as string | undefined,
            // Add reservation fields for real-time updates
            reservation: data.reservation ? {
              userId: data.reservation.userId,
              timestamp: data.reservation.timestamp?.toDate() || new Date(),
              expiresAt: data.reservation.expiresAt?.toDate() || new Date()
            } : undefined,
            waitlist: data.waitlist || [],
            reservationHistory: data.reservationHistory || [],
            // Add discount field
            discount: data.discount ? {
                    absoluteAmount: data.discount.absoluteAmount,
                    procentualAccount: data.discount.procentualAccount,
                    newPrice: data.discount.newPrice,
                    oldPrice: data.discount.oldPrice,
                    discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
                    discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
                  } : undefined,
                  eventId: data.eventId as string | undefined,
                  eventExclusive: data.eventExclusive as boolean | undefined,
          }
          setFlash(flashData)
          setCollectionName("flashes")
          setError(null)
          setLoading(false)
        } else {
          // Flash not found in main collection, try archived
          const unsubscribeArchived = onSnapshot(
            doc(db, "flashesArchived", flashId),
            (archivedDoc) => {
              if (archivedDoc.exists()) {
                const data = archivedDoc.data()
                const flashData: PortfolioItem = {
                  id: archivedDoc.id,
                  artistId: data.artistId as string,
                  type: (data.type as PortfolioItem["type"]) ?? "image",
                  storagePath: (data.storagePath as string) ?? "",
                  imageUrl: data.imageUrl as string,
                  compressedImageUrl: data.compressedImageUrl as string | undefined,
                  width: (data.width as number) ?? 0,
                  height: (data.height as number) ?? 0,
                  styleTags: (data.styleTags as string[]) ?? [],
                  caption: (data.caption as string) ?? "",
                  createdAt: data.createdAt?.toDate?.() ?? new Date(),
                  isFlash: true,
                  minPrice: data.minPrice as string,
                  minSize: data.minSize as string,
                  fotw: data.fotw as boolean,
                  fotwWeek: data.fotwWeek as number,
                  votes: (data.votes as string[]) ?? [],
                  accessCode: data.accessCode as string | undefined,
                  reservation: data.reservation ? {
                    userId: data.reservation.userId,
                    timestamp: data.reservation.timestamp?.toDate() || new Date(),
                    expiresAt: data.reservation.expiresAt?.toDate() || new Date()
                  } : undefined,
                  waitlist: data.waitlist || [],
                  reservationHistory: data.reservationHistory || [],
                  discount: data.discount ? {
                    absoluteAmount: data.discount.absoluteAmount,
                    procentualAccount: data.discount.procentualAccount,
                    newPrice: data.discount.newPrice,
                    oldPrice: data.discount.oldPrice,
                    discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
                    discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
                  } : undefined,
                  eventId: data.eventId as string | undefined,
                  eventExclusive: data.eventExclusive as boolean | undefined,
                }
                setFlash(flashData)
                setCollectionName("flashesArchived")
                setError(null)
              } else {
                setFlash(null)
                setError(new Error("Flash not found"))
              }
              setLoading(false)
            },
            (err) => {
              console.error("Failed to load flash detail from archived", err)
              setError(err)
              setLoading(false)
            }
          )
          unsubscribe = unsubscribeArchived
        }
      },
      (err) => {
        console.error("Failed to load flash detail", err)
        setError(err)
        setLoading(false)
      }
    )

    unsubscribe = unsubscribeFlashes

    return () => unsubscribe?.()
  }, [flashId])

  return { flash, loading, error, collectionName }
}
